<template>
  <div class="print-or-save-dialog-container">
    <LfBlueTitleComponent :title="texts.title"/>
    <div class="step-wizard-container">
      <ejs-pdfviewer
        v-if="hasDocumentGenerated"
        id="pdf-viewer"
        :documentPath="documentPath"
        :resourceUrl="resourceUrl"
        :enableToolbar="false"
        :enableThumbnail="false"
        :enableNavigationToolbar="false"
      />
      <SpinnerLayerComponent
        v-else
        class="spinner-layer"
        :loadingText="texts.spinner"
      />
      <div class="dialog-actions-container">
        <LfActionsBarComponent
          class="action-buttons"
          :showKebabMenu="false"
          :buttons="buttons"
          @execute-action="onExecuteAction"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, PropType, ref } from 'vue'
import { IFacturaEDialogModel } from '@/components/billing/FacturaEDialog/types/FacturaEDialogTypes'
import LfBlueTitleComponent from '@/components/HeaderTitle/LfBlueTitleComponent.vue'
import LfActionsBarComponent from '@/components/ActionsBar/LfActionsBarComponent.vue'
import { LabelButtonVariant } from '@/components/forms/buttons/types/ButtonTypes'
import { ActionBarButton, ActionName } from '@/components/ActionsBar/types/ActionBarComponentTypes'
import useActionsFacturaEDialog from '@/components/billing/FacturaEDialog/composables/useActionsFacturaEDialog'
import SpinnerLayerComponent from '@/components/Spinner/SpinnerLayerComponent.vue'
import { useI18n } from 'vue-i18n-composable'

const props = defineProps({
  model: {
    type: Object as PropType<IFacturaEDialogModel>,
    required: true
  }
})

const { t } = useI18n()

const texts = {
  title: t('components.facturae_dialog.preview.title').toString(),
  download: t('action_buttons.download').toString(),
  prev: t('action_buttons.prev').toString(),
  cancel: t('action_buttons.cancel').toString(),
  send: t('action_buttons.send').toString(),
  spinner: t('components.spinner.generating_document').toString(),
}

const {
  close,
  resetDocument,
  downloadDocument,
  prev,
  next,
  generateDocument,
  hasDocumentGenerated,
} = useActionsFacturaEDialog(props.model)

const resourceUrl = ref(`${ process.env.VUE_APP_FRONT_URL }`)

const documentPath = computed(() => `data:application/pdf;base64,${ props.model?.documentContent }`)

const buttons = computed(() => {
  return [
    {
      tooltip: texts.cancel,
      variant: LabelButtonVariant.OUTLINED,
      action: ActionName.CLOSE,
      enabled: true
    },
    {
      tooltip: texts.prev,
      variant: LabelButtonVariant.OUTLINED,
      action: ActionName.PREV,
      enabled: true
    },
    {
      tooltip: texts.download,
      variant: LabelButtonVariant.OUTLINED,
      action: ActionName.DOWNLOAD,
      enabled: hasDocumentGenerated.value
    },
    {
      tooltip: texts.send,
      variant: LabelButtonVariant.SOLID,
      action: ActionName.SEND_FACTURA_E,
      enabled: hasDocumentGenerated.value
    }
  ] as ActionBarButton []
})

function onExecuteAction(action: string) {
  switch (action) {
    case ActionName.PREV:
      resetDocument()
      prev()
      return
    case ActionName.CLOSE:
      close()
      return
    case ActionName.DOWNLOAD:
      downloadDocument()
      return
    case ActionName.SEND_FACTURA_E:
      next()
      return
  }
}

onMounted(async () => await generateDocument())

</script>

<style scoped lang="scss">
.invoices-template-selector-container {
  ::v-deep .no-result-dialog {
    display: flex;
    text-align: center;
    justify-content: center;
    align-items: center;
    height: 343px;
    max-height: 100%;
  }
}

.invoices-template-selector-container {
  margin-top: 0;
  padding-top: 0;

  ::v-deep .v-treeview {
    .v-icon {
      &::before {
        font-size: 18px;
        color: $corporate-color;
      }

      &::after {
        display: none;
      }
    }

    .is-directory,
    .is-template {
      font-size: 14px;
    }

    .is-directory {
      color: $corporate-color;
    }

    .is-template {
      color: $black-01;
    }
  }
}

.step-wizard-container {
  width: 100%;

  .step-title {
    @include milano-bold-18;
    color: $main-1000;
  }

  .step-instructions {
    @include milano-regular-14;
    color: $neutral-grey-800;
    margin-top: $spacing-xs;
  }

  .static-alert {
    margin-top: $spacing-md;
  }
}

.dialog-actions-container {
  @include flex($justify-content: flex-end);
  width: 100%;
  margin-top: $spacing-xl;

  ::v-deep .main-action-button {
    @include main-action-button--rectangle;
  }

  ::v-deep .secondary-action-button {
    @include secondary-action-button--rectangle;
  }
}
</style>
